import { salesloftMessageHandler } from 'services/windowMessaging/messageHandlers/salesloftMessageHandler';

import type { WidgetItemId } from './domain/company_widgets';
import { widgetItemIds } from './domain/company_widgets';
import { Calendar } from './domain/meeting';
import type { EventData } from './domain/message';
import { EventMessages, isEventData } from './domain/message';
import { ENABLE_GCALENDAR_KEY, ENABLE_TOOLS_KEY } from './domain/settings';
import type { AppStore } from './services/appStore';
import TrackingMessageEventsService from './services/tracking/TrackingMessageEventsService';
import UrlService from './services/UrlService';
import { calendarMessageHandler } from './services/windowMessaging/messageHandlers/calendarMessageHandler';
import { gmailMessageHandler } from './services/windowMessaging/messageHandlers/gmailMessageHandler';
import { googleMessageHandler } from './services/windowMessaging/messageHandlers/googleMessageHandler';
import { googleSheetsMessageHandler } from './services/windowMessaging/messageHandlers/googleSheetsMessageHandler';
import { hubspotMessageHandler } from './services/windowMessaging/messageHandlers/hubspotMessageHandler';
import { linkedinMessageHandler } from './services/windowMessaging/messageHandlers/linkedinMessageHandler';
import { microsoftDynamicsMessageHandler } from './services/windowMessaging/messageHandlers/microsoftDynamicsMessageHandler';
import { salesforceLightningMessageHandler } from './services/windowMessaging/messageHandlers/salesforceLightningMessageHandler';
import { salesforceMessageHandler } from './services/windowMessaging/messageHandlers/salesforceMessageHandler';
import { settingsMessageHandler } from './services/windowMessaging/messageHandlers/settingsMessageHandler';

export type Commands = Pick<
  AppStore,
  | 'setCompanyPage'
  | 'setContactPage'
  | 'setSearchPage'
  | 'setCompanyLinkedinId'
  | 'setCompanyDomain'
  | 'setCollectedContactInfo'
  | 'setCollectedContactsSearchInfo'
  | 'setCompanyTabName'
  | 'setHomePage'
  | 'setIsOpen'
  | 'setUrl'
  | 'setSettingsStorageValue'
  | 'getSettingsStorageValue'
  | 'setSearchPagePagination'
  | 'setCalendarPage'
  | 'setCollectedMeeting'
  | 'resetCollectedMeeting'
  | 'getCollectedMeeting'
  | 'getState'
  | 'setSettingsPage'
  | 'setCollectedEmail'
  | 'setEmailPage'
  | 'setGoogleSheetsPage'
  | 'setCollectedDomains'
  | 'setWidgetId'
> & {
  postConfig: () => void;
  postBadgeVisibilityConfig: () => void;
};

export const makeMessageHandler = (commands: Commands) => {
  return async (event: EventData) => {
    if (!isEventData(event)) {
      return;
    }

    TrackingMessageEventsService.trackMessageEvent(event);

    if (event.message === EventMessages.REQUEST_CONFIG) {
      commands.postConfig();
      return;
    }

    if (event.message === EventMessages.REQUEST_BADGE_VISIBILITY_CONFIG) {
      commands.postBadgeVisibilityConfig();
      return;
    }

    if (event.message === EventMessages.TOGGLE_VISIBILITY) {
      commands.setIsOpen(event.isOpen);
    }

    if (
      event.message === EventMessages.TOGGLE_EMAIL_AGENT_VISIBILITY ||
      event.message === EventMessages.LOGIN_AND_OPEN_EMAIL_AGENT
    ) {
      return;
    }

    const url = UrlService.createEventUrl(event.url);

    if (event.message === EventMessages.SET_DOMAIN) {
      commands.setUrl(event.url);
      commands.setCompanyDomain(event.domain);
      commands.setCompanyPage({ companyDomain: event.domain });
      commands.setIsOpen(true);

      if (event.widgetId && widgetItemIds.includes(event.widgetId as WidgetItemId)) {
        commands.setWidgetId(event.widgetId as WidgetItemId);
      }
      return;
    }

    const isOutlookCalendar = UrlService.isOutlookCalendarUrl(url);
    const isGoogleCalendar = UrlService.isGoogleCalendarUrl(url);
    const isCalendar = isGoogleCalendar || isOutlookCalendar;
    const isLinkedin = UrlService.isLinkedinUrl(url);
    const isSalesloft = UrlService.isSalesloftUrl(url);
    const isHubspot = UrlService.isHubspotUrl(url);
    const isMicrosoftDynamics = UrlService.isMicrosoftDynamicsUrl(url);
    const isSalesforce = UrlService.isSalesforceUrl(url);
    const isSalesforceLightning = UrlService.isSalesforceLightningUrl(url);
    const isGmail = UrlService.isGmailUrl(url);
    const isGoogleSheets = UrlService.isGoogleSheetsUrl(url);
    const isGoogleLocalDev = UrlService.isGoogleLocalDevUrl(url);

    if (event.message === EventMessages.SET_SETTINGS_STORAGE) {
      const { settingsStorage } = event;
      await settingsMessageHandler.handleSetSettingsStorage(commands, settingsStorage);

      if (isLinkedin) {
        linkedinMessageHandler.handleAutoOpen(commands, url);
      }
    }

    if (event.message === EventMessages.IFRAME_LOADED || event.message === EventMessages.URL_CHANGED) {
      commands.setUrl(event.url);

      const HANDLERS = [
        {
          condition: isLinkedin,
          handle: () => {
            const settingsWereSet = commands.getSettingsStorageValue('settingsWereSet');
            if (settingsWereSet) {
              linkedinMessageHandler.handleAutoOpen(commands, url);
            }
            linkedinMessageHandler.handleIframeLoad(commands, url);
          },
        },
        { condition: isCalendar, handle: () => calendarMessageHandler.handleIframeLoad(commands) },
        {
          condition: isSalesloft,
          handle: () => salesloftMessageHandler.handleIframeLoad(commands, url),
        },
        {
          condition: isHubspot,
          handle: () => hubspotMessageHandler.handleIframeLoad(commands, url),
        },
        {
          condition: isMicrosoftDynamics,
          handle: () => microsoftDynamicsMessageHandler.handleIframeLoad(commands, url),
        },
        {
          condition: isSalesforce,
          handle: () => salesforceMessageHandler.handleIframeLoad(commands, url),
        },
        {
          condition: isSalesforceLightning,
          handle: () => salesforceLightningMessageHandler.handleIframeLoad(commands, url),
        },
        {
          condition: isGmail,
          handle: () => gmailMessageHandler.handleIframeLoad(commands),
        },
        {
          condition: isGoogleSheets,
          handle: () => googleSheetsMessageHandler.handleIframeLoad(commands),
        },
        {
          condition: isGoogleLocalDev,
          handle: () => googleMessageHandler.handleIframeLoad(commands, url),
        },
      ];

      const handler = HANDLERS.find(({ condition }) => condition);
      if (handler) {
        handler.handle();
      } else {
        commands.setCompanyPage({ companyDomain: url ? UrlService.removeWWW(url.hostname) : '' });
      }
    }

    if (event.message === EventMessages.DATA_CHANGED) {
      const DATA_CHANGED_HANDLERS = [
        {
          condition: isOutlookCalendar && commands.getSettingsStorageValue(ENABLE_TOOLS_KEY),
          onHandle: () => calendarMessageHandler.handleDataChange(commands, event, Calendar.outlook, url),
        },
        {
          condition: isGoogleCalendar && commands.getSettingsStorageValue(ENABLE_GCALENDAR_KEY),
          onHandle: () => calendarMessageHandler.handleDataChange(commands, event, Calendar.google, url),
        },
        {
          condition: isLinkedin,
          onHandle: () => linkedinMessageHandler.handleDataChange(commands, event, url),
        },
        {
          condition: isSalesloft,
          onHandle: () => salesloftMessageHandler.handleDataChange(commands, event, url),
        },
        {
          condition: isHubspot,
          onHandle: () => hubspotMessageHandler.handleDataChange(commands, event, url),
        },
        {
          condition: isMicrosoftDynamics,
          onHandle: () => microsoftDynamicsMessageHandler.handleDataChange(commands, event, url),
        },
        {
          condition: isSalesforce,
          onHandle: () => salesforceMessageHandler.handleDataChange(commands, event, url),
        },
        {
          condition: isSalesforceLightning,
          onHandle: () => salesforceLightningMessageHandler.handleDataChange(commands, event, url),
        },
        {
          condition: isGmail,
          onHandle: () => gmailMessageHandler.handleDataChange(commands, event, url),
        },
        {
          condition: isGoogleSheets,
          onHandle: () => googleSheetsMessageHandler.handleDataChange(commands, event),
        },
        {
          condition: isGoogleLocalDev,
          onHandle: () => googleMessageHandler.handleDataChange(commands),
        },
      ];

      const dataChangedHandler = DATA_CHANGED_HANDLERS.find(({ condition }) => condition);
      dataChangedHandler?.onHandle();
    }
  };
};
